/**
 * Typed failure for every rejection in the songpack layer.
 *
 * Callers (and tests) branch on `code`, never on `message`. Messages are for
 * humans and may be reworded; codes are part of the module contract.
 */

export type SongPackErrorCode =
  // --- archive structure -----------------------------------------------------
  /** Not a readable ZIP container at all. */
  | 'INVALID_ARCHIVE'
  /** Entry is not stored at the archive root (Unity: `Name != FullName`). */
  | 'ENTRY_NOT_AT_ROOT'
  /** Entry name contains `..` (ZIP Slip). */
  | 'ENTRY_PATH_TRAVERSAL'
  /** Two entries resolve to the same name, compared case-insensitively. */
  | 'DUPLICATE_ENTRY'
  /** Archive declares more entries than a Song Pack can legitimately hold. */
  | 'TOO_MANY_ENTRIES'
  /** A single entry exceeds its size budget. */
  | 'ENTRY_TOO_LARGE'
  /** Declared uncompressed total exceeds the archive budget (zip bomb guard). */
  | 'ARCHIVE_TOO_LARGE'
  /** Entry uses a compression method outside {store, deflate}. */
  | 'UNSUPPORTED_COMPRESSION'
  /** A required entry is absent. */
  | 'MISSING_ENTRY'
  // --- payload ---------------------------------------------------------------
  /** Entry is not decodable UTF-8 JSON. */
  | 'INVALID_JSON'
  /** `formatVersion` is not 1. Unknown versions are rejected, never guessed. */
  | 'UNSUPPORTED_FORMAT_VERSION'
  /** `manifest.json` violates a version 1 rule. */
  | 'INVALID_MANIFEST'
  /** `packId` or `songId` is empty, too long, or has an illegal character. */
  | 'INVALID_ID'
  /** `manifest.charts` is not exactly the three version 1 references. */
  | 'INVALID_CHART_REFERENCE'
  /** `music.wav` is not RIFF/WAVE. */
  | 'INVALID_MUSIC'
  /** `cover.png` is not a PNG. */
  | 'INVALID_COVER'
  /** A bundled music video is not an MP4 or WebM container. */
  | 'INVALID_VIDEO'
  /** A `.chart` document violates a version 1 rule. */
  | 'INVALID_CHART'
  /** A chart carries more events than version 1 permits. */
  | 'TOO_MANY_EVENTS'
  /** A wave event field is structurally wrong. */
  | 'INVALID_EVENT'
  /** A numeric field is NaN or Infinity. */
  | 'NON_FINITE_VALUE';

export interface SongPackErrorDetails {
  /** Entry name the failure relates to, when applicable. */
  readonly entry?: string;
  /** Dotted path inside the JSON document, e.g. `events[12].speed`. */
  readonly path?: string;
  /** Underlying cause, for archive-level failures. */
  readonly cause?: unknown;
}

export class SongPackError extends Error {
  readonly code: SongPackErrorCode;
  readonly entry: string | undefined;
  readonly path: string | undefined;

  constructor(code: SongPackErrorCode, message: string, details: SongPackErrorDetails = {}) {
    super(message, details.cause === undefined ? undefined : { cause: details.cause });
    this.name = 'SongPackError';
    this.code = code;
    this.entry = details.entry;
    this.path = details.path;
  }

  override toString(): string {
    const where = [this.entry, this.path].filter((part) => part !== undefined).join(' ');
    return where.length > 0
      ? `SongPackError[${this.code}] ${this.message} (${where})`
      : `SongPackError[${this.code}] ${this.message}`;
  }
}

export function isSongPackError(value: unknown): value is SongPackError {
  return value instanceof SongPackError;
}

/**
 * Always throws. Declared as returning `never` rather than as an assertion
 * signature so it stays usable across module boundaries without forcing explicit
 * type annotations on every call site.
 */
export function fail(
  code: SongPackErrorCode,
  message: string,
  details?: SongPackErrorDetails,
): never {
  throw new SongPackError(code, message, details);
}
